import React, { useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useTheme } from '../../contexts/ThemeContext';
import { resolveCategoryOrder, resolveCategoryStyle, CATEGORY_BLUE_SCALE, formatHours } from './constants';
import { useTranslation } from 'react-i18next';

ChartJS.register(ArcElement, Tooltip, Legend);

const CATEGORY_LABEL_KEYS = {
  HNO: 'summaryHno',
  HO: 'summaryHo',
  Astreinte: 'summaryAstreinte',
  Formation: 'summaryFormation',
  Congés: 'summaryConges',
  Absence: 'summaryAbsence',
};

function categoryLabel(t, category, fallback) {
  const key = CATEGORY_LABEL_KEYS[category];
  return key ? t(key) : fallback;
}


export default function NonProductiveRatesCard({ rates = [], totalHours = 0, categories = null }) {
  const { t } = useTranslation('planning');
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [excluded, setExcluded] = useState(() => new Set());

  const categoryOrder = useMemo(() => resolveCategoryOrder(categories), [categories]);
  const categoryStyle = useMemo(() => resolveCategoryStyle(categories), [categories]);

  const rateMap = useMemo(
    () => Object.fromEntries((rates || []).map((rate) => [rate.category, rate])),
    [rates]
  );

  const allSlices = useMemo(
    () =>
      categoryOrder.map((category, index) => {
        const style = categoryStyle[category] || { label: category };
        const entry = rateMap[category];
        return {
          category,
          label: categoryLabel(t, category, style.label),
          color: CATEGORY_BLUE_SCALE[index % CATEGORY_BLUE_SCALE.length],
          hours: Number(entry?.hours || 0),
        };
      }),
    [rateMap, categoryOrder, categoryStyle, t]
  );

  const visibleSlices = useMemo(
    () => allSlices.filter((item) => !excluded.has(item.category)),
    [allSlices, excluded]
  );

  const visibleTotal = useMemo(
    () => visibleSlices.reduce((sum, item) => sum + item.hours, 0),
    [visibleSlices]
  );

  const sliceMeta = useMemo(
    () =>
      allSlices.map((item) => {
        const isExcluded = excluded.has(item.category);
        const percent =
          !isExcluded && visibleTotal > 0 ? (item.hours / visibleTotal) * 100 : 0;
        return {
          ...item,
          isExcluded,
          percent,
        };
      }),
    [allSlices, excluded, visibleTotal]
  );

  const sourceTotal = Number(totalHours || 0);
  const hasSourceData = sourceTotal > 0;
  const hasVisibleData = visibleTotal > 0;

  const segmentBorder = isDark ? '#0b1327' : '#ffffff';
  const emptySliceColor = isDark ? '#334155' : '#e2e8f0';

  const data = useMemo(
    () => ({
      labels: visibleSlices.map((item) => item.label),
      datasets: [
        {
          data: hasVisibleData ? visibleSlices.map((item) => item.hours) : [1],
          backgroundColor: hasVisibleData
            ? visibleSlices.map((item) => item.color)
            : [emptySliceColor],
          borderColor: segmentBorder,
          borderWidth: 2,
          hoverOffset: hasVisibleData ? 4 : 0,
        },
      ],
    }),
    [emptySliceColor, hasVisibleData, segmentBorder, visibleSlices]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: hasVisibleData,
          backgroundColor: isDark ? '#1e293b' : '#111418',
          titleColor: '#ffffff',
          bodyColor: '#e2e8f0',
          padding: 10,
          callbacks: {
            label: (ctx) => {
              const meta = visibleSlices[ctx.dataIndex];
              if (!meta) return ` ${ctx.label}`;
              const hours = formatHours(meta.hours || ctx.parsed || 0);
              const percent =
                visibleTotal > 0 ? ((meta.hours / visibleTotal) * 100).toFixed(1) : '0.0';
              return ` ${ctx.label}: ${hours} hrs (${percent}%)`;
            },
          },
        },
      },
    }),
    [hasVisibleData, isDark, visibleSlices, visibleTotal]
  );

  const toggleCategory = (category) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  return (
    <div className="bg-white p-5 rounded-xl border border-[#e5e7eb] shadow-sm space-y-4 min-w-0 h-full flex flex-col">
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wide text-[#617289]">{t('productionRates')}</h3>
        <p className="text-[11px] text-[#617289] mt-1">
          Share of selected categories ({formatHours(visibleTotal)} hrs
          {excluded.size > 0 ? ` · ${visibleSlices.length}/${allSlices.length} shown` : ''})
        </p>
      </div>

      {!hasSourceData ? (
        <div className="flex-1 flex items-center justify-center py-10">
          <p className="text-sm font-medium text-[#94a3b8]">{t('noHoursInRange')}</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-center">
            <div className="relative w-48 h-48">
              <Doughnut data={data} options={options} />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pointer-events-none">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[#617289]">
                  Total
                </span>
                <span className="text-2xl font-black tracking-tight text-[#124191]">
                  {formatHours(visibleTotal)}h
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-1.5 text-xs" role="listbox" aria-label={t('categoryFilter')}>
            {sliceMeta.map((item) => (
              <button
                key={item.category}
                type="button"
                role="option"
                aria-selected={!item.isExcluded}
                title={item.isExcluded ? t('includeLabel', { label: item.label }) : t('excludeLabel', { label: item.label })}
                onClick={() => toggleCategory(item.category)}
                className={`production-rate-legend-item${item.isExcluded ? ' is-excluded' : ''}`}
              >
                <span className="inline-flex items-center gap-2 text-left min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{
                      backgroundColor: item.isExcluded ? '#cbd5e1' : item.color,
                    }}
                    aria-hidden="true"
                  />
                  <span className="truncate font-medium">{item.label}</span>
                </span>
                <span className="font-semibold whitespace-nowrap">
                  {item.isExcluded ? 'Off' : `${item.percent.toFixed(1)}%`}
                  {!item.isExcluded && (
                    <span className="text-[#617289] font-medium"> · {formatHours(item.hours)}h</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
